import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { LanguageService } from '@klickdata/core/localization';
import { MessageService } from '@klickdata/core/message';
import { ResourceService, ResourceSocketService, ResourceTypes } from '@klickdata/core/resource';
import { AiModelSpecs, MaterialModelType, Resource } from '@klickdata/core/resource/src/resource.model';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
declare var webkitSpeechRecognition;

@Component({
    selector: 'app-ai-prompter-manager',
    templateUrl: './ai-prompter-manager.component.html',
    styleUrls: ['./ai-prompter-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiPrompterManagerComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    @Input() model: AiModelSpecs; // llm name
    @Input() resource: Resource;
    @Input() placeholder = $localize`Write a prompter text to ask AI anything`;
    @Input() advancedModeSub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Output() onListMaterial: EventEmitter<any> = new EventEmitter();
    @Output() onMaterialCreated: EventEmitter<Resource> = new EventEmitter();
    @Output() onMaterialEdited: EventEmitter<Resource> = new EventEmitter();
    @Output() onMaterialTypeChanged: EventEmitter<MaterialModelType> = new EventEmitter();
    @ViewChild('input') input: ElementRef<HTMLInputElement>;
    loading = false;
    public prompterLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * Id of the material the user just asked the AI to create. We keep it so we
     * can re-check that material directly if the live connection drops (iPhone
     * Safari) before the "it's ready" event arrives.
     */
    private pendingPromptResourceId: number | null = null;
    showTags = false;
    showQuiz = false;
    MaterialModelType = MaterialModelType;
    promptForm: FormGroup;
    public destroy: Subject<boolean> = new Subject<boolean>();
    public currentResource$: BehaviorSubject<Resource> = new BehaviorSubject(null);
    public vtqData: {
        question_count: number;
        difficulty_level: number;
        create_course: boolean;
        create_material: boolean;
        create_quiz: boolean;
        question_type_value: string;
    };
    public authUser$: Observable<User>;

    public userLangId$: Observable<number>;
    constructor(
        protected fb: FormBuilder,
        protected resourceService: ResourceService,
        protected resourceWS: ResourceSocketService,
        protected message: MessageService,
        protected router: Router,
        protected cdr: ChangeDetectorRef,
        protected auth: AuthService,
        protected langService: LanguageService
    ) {
        this.promptForm = this.fb.group({
            prompt: null,
            item_type_value: [MaterialModelType.TEXT],
            type_id: [ResourceTypes.GeneralMaterial],
            tag_ids: [[]],
            metadata: this.fb.group({
                voice_name: [null],
            }),
        });
        this.authUser$ = this.auth.getUser().pipe(
            tap((user) => {
                const promptLoader =
                    !!user.prompt_states?.length &&
                    user.prompt_states?.find(
                        (state) => state.type === 'prompt' && Utils.isOnProgressPromptStatus(state.status)
                    );
                this.prompterLoading.next(!!promptLoader);
            })
        );
    }

    ngOnInit(): void {
        this.listenToNewResource();
        this.recoverPromptStateOnReconnect();
        this.promptForm.valueChanges.pipe(takeUntil(this.destroy)).subscribe(() => this.updateLastPrompterValues());

        this.promptForm
            .get('item_type_value')
            .valueChanges.pipe(takeUntil(this.destroy))
            .subscribe((value) => {
                this.onMaterialTypeChanged.emit(value);
            });

        this.promptForm.patchValue({
            prompt: this.resourceService.lastPrompterValues.prompt,
            tag_ids: this.resourceService.lastPrompterValues.tag_ids,
        });
        this.userLangId$ = this.langService
            .getLanguageByKey(this.langService.getCurrentLanguage().value)
            .pipe(map((lang) => lang.id));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['model']?.currentValue) {
            this.model = changes['model'].currentValue;
            this.cdr.markForCheck();
        }
    }

    private updateLastPrompterValues() {
        this.resourceService.lastPrompterValues = this.promptForm.value;
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.input?.nativeElement?.focus();
        }, 1000);
    }

    getSubmitBtnTooltip(): string {
        return this.promptForm.value.prompt
            ? $localize`Send your request to AI and wait for the View to lid to read the reply`
            : $localize`Create Material with AI`;
    }

    addMaterial() {
        this.loading = true;
        this.createPromptObservable()
            .pipe(takeUntil(this.destroy))
            .subscribe((resource) => {
                const createdResource = Array.isArray(resource) ? resource[0] : resource;
                this.pendingPromptResourceId = createdResource?.id ?? null;
                this.showQuiz = false;
                this.vtqData = null;
                this.loading = false;
                this.onMaterialCreated.emit(createdResource);
            });
        this.cleanInput();
    }

    /**
     * Recover the prompter when the live connection drops and comes back
     * (a common iPhone Safari situation: the user leaves the app, iOS closes
     * the connection, and the "your AI material is ready" message never arrives).
     *
     * Without this, the loading animation would spin forever and the "View"
     * button would stay disabled even though the material was actually created.
     *
     * When the connection is restored while we are still waiting, we ask the
     * server again for the latest status: if the AI has finished we stop the
     * loader and enable the "View" button, otherwise we keep waiting.
     */
    private recoverPromptStateOnReconnect(): void {
        this.auth
            .onSocketReconnected()
            .pipe(
                filter(() => this.prompterLoading.value),
                switchMap(() => this.auth.refreshUser().pipe(catchError(() => EMPTY))),
                takeUntil(this.destroy)
            )
            .subscribe((user) => {
                const stillGenerating =
                    !!user.prompt_states?.length &&
                    user.prompt_states.some(
                        (state) => state.type === 'prompt' && Utils.isOnProgressPromptStatus(state.status)
                    );

                if (stillGenerating) {
                    return;
                }

                this.prompterLoading.next(false);

                if (this.pendingPromptResourceId && !this.currentResource$.value) {
                    this.resourceService
                        .getResource(this.pendingPromptResourceId)
                        .pipe(
                            catchError(() => EMPTY),
                            takeUntil(this.destroy)
                        )
                        .subscribe((resource) => {
                            this.currentResource$.next(resource);
                            this.pendingPromptResourceId = null;
                            this.cdr.markForCheck();
                        });
                }

                this.cdr.markForCheck();
            });
    }
    private createPromptObservable() {
        const data = this.prepareData();
        return this.resourceService.prompter(data, this.resource?.id);
    }
    private prepareData() {
        const data = { ...this.promptForm.value, model_name: this.model?.value };
        if (!!this.vtqData?.create_quiz) {
            data.ai_metadata = {
                question_count: this.vtqData?.question_count,
                difficulty_level: this.vtqData?.difficulty_level,
                question_type_value: this.vtqData?.question_type_value,
            };
        }
        if (!!this.vtqData?.create_material) {
            data.create_material = true;
        }
        if (!!this.vtqData?.create_course) {
            data.create_course = true;
        }
        return data;
    }
    public listingMaterial(user: User) {
        this.onListMaterial.emit();
        this.router.navigate([!user.isAdmin() ? '/home/manage/material/list' : '/admin/content/materials/list'], {
            queryParams: {
                bypassGuardWithoutSaving: true,
            },
        });
        this.currentResource$.next(null);
    }
    private cleanInput() {
        const promptControl = <FormControl>this.promptForm.get('prompt');
        promptControl.setValue('');
        this.resourceService.lastPrompterValues.prompt = '';
        FormHelper.resetForm(promptControl);
        setTimeout(() => {
            this.input?.nativeElement?.focus();
        }, 1000);
    }
    public listenToNewResource() {
        return this.resourceWS
            .listenToUserResourcesFromAi()
            .pipe(takeUntil(this.destroy))
            .subscribe((resource) => {
                this.currentResource$.next(resource);
                this.pendingPromptResourceId = null;
            });
    }

    editMaterial() {
        this.onMaterialEdited.emit(this.currentResource$.value);
        this.currentResource$.next(null);
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
